import { EditIcon, FlagIcon, TrashIcon } from "@xola/icons";
import { formatDate } from "@xola/ui-kit";
import clsx from "clsx";
import { orderBy } from "lodash";
import React from "react";
import { Event, Note, Permission, Purchase, User } from "types";
import { useSeller } from "../../hooks/useAccount";
import { useAuthorization } from "../../hooks/useAuthorization";
import { useResource } from "../../hooks/useResource";
import { useVisibility } from "../../hooks/useVisibility";
import { getCustomerName } from "../../models/Purchase";
import { CreateCustomerNoteModal } from "../Customers/Contacts/CreateCustomerNoteModal";
import { InlineConfirmDeletePopover } from "./InlineConfirmDeletePopover";
import { NoTranslate } from "./NoTranslate";
import { SecondaryButton } from "./SecondaryButton";

interface NoteItemProps {
    note: Note;
    customerName?: string;
    userId?: ID;
    heading?: string;
    purchaseId?: ID;
    eventId?: ID;
    onDelete?: (noteId: ID) => void;
    readOnly?: boolean;
}

const NoteItem = ({
    note,
    customerName,
    userId,
    heading,
    purchaseId,
    eventId,
    onDelete,
    readOnly = false,
}: NoteItemProps) => {
    const { data: updatedBy } = useResource<User>(["users", note.updatedBy?.id], null, {
        // Sometimes, because of some bad data, `note.updatedBy` does not exist. Causing this to throw "TypeError: cannot read "id" of undefined. To prevent calling API in this scenario, `enabled` is added
        enabled: !!note.updatedBy?.id,
    });
    const [showModal, { toggle: toggleModal }] = useVisibility(false);

    return (
        <>
            <li className="text-gray-darker" data-testid="note-list-item">
                <div className="flex items-center justify-between text-black">
                    <p
                        className="align-items text-sm font-bold leading-p1 tracking-tightest"
                        data-testid="note-list-customer-name"
                    >
                        {customerName ? (
                            <>
                                <FlagIcon className="mr-1.5" /> <NoTranslate>{customerName}</NoTranslate>
                            </>
                        ) : (
                            "Staff Note"
                        )}
                    </p>
                    <div className="flex items-center space-x-1">
                        <NoTranslate as="p" className="text-sm leading-p3">
                            {formatDate(note.updated, "LLL")} {updatedBy?.name ? `- ${updatedBy.name}` : ""}
                        </NoTranslate>
                        {!readOnly && (
                            <SecondaryButton size="tiny" data-testid="note-list-edit-button" onClick={toggleModal}>
                                <EditIcon />
                            </SecondaryButton>
                        )}
                        {onDelete && !readOnly && (
                            <InlineConfirmDeletePopover confirmButtonText="Confirm" onConfirm={() => onDelete(note.id)}>
                                <SecondaryButton data-testid="note-list-delete-button" size="tiny">
                                    <TrashIcon />
                                </SecondaryButton>
                            </InlineConfirmDeletePopover>
                        )}
                    </div>
                </div>
                <NoTranslate
                    as="p"
                    className="mt-1 whitespace-pre-wrap break-all leading-p1 tracking-tightest"
                    data-testid="note-list-note-content"
                >
                    {note.text}
                </NoTranslate>
            </li>
            <CreateCustomerNoteModal
                isOpen={showModal}
                customerId={userId ?? ""}
                note={note}
                heading={heading}
                purchaseId={purchaseId}
                eventId={eventId}
                onClose={toggleModal}
            />
        </>
    );
};

interface NoteListProps {
    notes: Note[];
    customerName?: string;
    className?: string;
    userId?: ID;
    purchaseId?: ID;
    eventId?: ID;
    heading?: string;
    onDelete?: (noteId: ID) => void;
    readOnly?: boolean;
}

export const NoteList = ({
    notes,
    customerName,
    className,
    userId,
    purchaseId,
    eventId,
    heading = "Customer",
    onDelete,
    readOnly = false,
}: NoteListProps) => {
    return (
        <ul className={clsx("space-y-4", className)}>
            {notes.map((note) => (
                <NoteItem
                    key={note.id}
                    note={note}
                    heading={heading}
                    customerName={customerName}
                    userId={userId}
                    purchaseId={purchaseId}
                    eventId={eventId}
                    readOnly={readOnly || note.system}
                    onDelete={onDelete}
                />
            ))}
        </ul>
    );
};

interface EventNoteListProps {
    event: Event;
    className?: string;
    userId?: ID;
    onDelete?: (noteId: ID) => void;
}

export const EventNoteList = ({ event, className, userId, onDelete }: EventNoteListProps) => {
    const notes = orderBy(event.notes, "updated", "desc");
    const { isGranted } = useAuthorization();

    const isReadOnlyNotes = !isGranted(Permission.ADD_EVENT_NOTE);

    return (
        <div data-testid="note-list-event-notes">
            <NoteList
                notes={notes}
                heading="Event"
                userId={userId}
                eventId={event.id}
                className={clsx("max-h-50 overflow-y-auto", className)}
                readOnly={isReadOnlyNotes}
                onDelete={onDelete}
            />
        </div>
    );
};

interface PurchaseNoteListProps {
    purchase: Purchase;
    className?: string;
    userId?: ID;
    onDelete?: (noteId: ID) => void;
}

export const PurchaseNoteList = ({ purchase, className, userId, onDelete }: PurchaseNoteListProps) => {
    const notes = orderBy(purchase.notes, "updated", "desc");

    const { isGranted } = useAuthorization();

    const isReadOnlyNotes = !isGranted(Permission.MODIFY_NOTES);
    return (
        <div className="pb-4" data-testid="note-list-purchase-notes">
            <NoteList
                notes={notes}
                heading="Purchase"
                userId={userId}
                purchaseId={purchase.id}
                customerName={getCustomerName(purchase)}
                className={className}
                readOnly={isReadOnlyNotes}
                onDelete={onDelete}
            />
        </div>
    );
};

interface CustomerNoteListProps {
    userId: string;
    className?: string;
    onDelete?: (noteId: ID) => void;
    readOnly?: boolean;
}

export const CustomerNoteList = ({ userId, className, onDelete, readOnly }: CustomerNoteListProps) => {
    const seller = useSeller();
    const customerNotes: Note[] = Object.values(seller?.customerNotes[userId]) ?? [];
    const notes = orderBy(customerNotes, "updated", "desc");

    return (
        <div data-testid="note-list-customer-notes">
            <NoteList
                notes={notes}
                userId={userId}
                className={clsx("max-h-50 overflow-y-auto", className)}
                readOnly={readOnly}
                onDelete={onDelete}
            />
        </div>
    );
};
